import { FC, ReactNode, useEffect, useMemo, useState } from 'react';

import { Col, Row, Skeleton } from 'antd';

import Attachment from 'components/Attachment';
import UploadFileWithButton from 'components/UploadFileWithButton/UploadFileWithButton';

import useWindowDimensions from 'hooks/useWindowDimensions';

import { AttachmentTypes } from 'types/api';
import { Attachment as AttachmentModel } from 'types/commons';

import { GlobalModules } from 'constants/enums';
import { commonFileTypes } from 'constants/jobseekerSettings';

import styles from './AttachmentsComponent.module.scss';

const COLUMNS_BREAKPOINT_PX = 1200;

export interface LabelForChildId {
  id: string;
  label: string | ReactNode;
}

type AttachmentsComponentProps = {
  onChange: () => void;
  attachmentType: AttachmentTypes;
  module?: GlobalModules;
  direction?: 'vertical' | 'horizontal' | 'columns';
  ownerId?: string;
  childId?: string;
  editable?: boolean;
  attachmentList?: AttachmentModel<AttachmentTypes>[] | null;
  allowedFileTypes?: string;
  label?: string;
  isHideUpload?: boolean;
  isConfirmDelete?: boolean;
  isPreviewFile?: boolean;
  disabled?: boolean;
  isDeleteDisabled?: boolean;
  bottomLabel?: string | ReactNode;
  bottomLabelIfChildId?: string | ReactNode;
  bottomLabelsForChildIds?: LabelForChildId[];
};

const AttachmentsComponent: FC<AttachmentsComponentProps> = ({
  direction = 'vertical',
  attachmentList,
  editable,
  ownerId,
  childId,
  attachmentType,
  allowedFileTypes = commonFileTypes,
  onChange,
  label,
  isHideUpload = false,
  isConfirmDelete = false,
  isPreviewFile = false,
  module,
  disabled = false,
  isDeleteDisabled = false,
  bottomLabel = null,
  bottomLabelIfChildId = null,
  bottomLabelsForChildIds
}) => {
  const [isShowSkeleton, setIsShowSkeleton] = useState(false);
  const { width } = useWindowDimensions();

  const handleSkeletonOpen = () => {
    setIsShowSkeleton(true);
  };

  const handleSkeletonClose = () => {
    setIsShowSkeleton(false);
  };

  useEffect(() => {
    handleSkeletonClose();
  }, [attachmentList]);

  const attachments = useMemo(
    () =>
      attachmentList
        ?.filter((attachment) => attachment.type === attachmentType)
        .sort((a, b) => {
          return +new Date(b.createdDate) - +new Date(a.createdDate);
        }),
    [attachmentList, attachmentType]
  );

  let colClassName = '';
  let colSpan = 24;

  switch (direction) {
    case 'vertical':
      colClassName = styles.col;
      break;
    case 'horizontal':
      colClassName = styles.colHorizontal;
      break;
    case 'columns':
      colSpan = width > COLUMNS_BREAKPOINT_PX ? 12 : 24;
      break;
    default:
      colClassName = styles.col;
  }

  const isShowUpload = Boolean(ownerId && !isHideUpload);

  return (
    <Row
      className={direction === 'vertical' ? styles.row : styles.rowHorizontal}
      gutter={[8, 8]}
    >
      {label && (
        <Col span={24} className={styles.label}>
          {label}
        </Col>
      )}
      {isShowSkeleton && (
        <Col className={colClassName} span={colSpan}>
          <Skeleton.Input active size="large" block />
        </Col>
      )}
      {attachments?.map((attachment) => {
        const attachmentBottomLabel = attachment?.childId
          ? bottomLabelsForChildIds?.find((el) => el.id === attachment.childId)
              ?.label
          : bottomLabelIfChildId || bottomLabel;

        return (
          <Col key={attachment.id} className={colClassName} span={colSpan}>
            <Attachment
              attachment={attachment as AttachmentModel<AttachmentTypes>}
              onRemoveFile={onChange}
              isConfirmDelete={isConfirmDelete}
              isPreviewFile={isPreviewFile}
              disabled={disabled}
              isDeleteDisabled={isDeleteDisabled}
              bottomLabel={attachmentBottomLabel}
            />
          </Col>
        );
      })}
      {isShowUpload && (
        <Col className={colClassName} span={colSpan}>
          <UploadFileWithButton
            allowedFileTypes={allowedFileTypes}
            attachmentType={attachmentType}
            ownerId={ownerId as string}
            childId={childId}
            onAddFile={onChange}
            onUploadStart={handleSkeletonOpen}
            onUploadEnd={handleSkeletonClose}
            isEditable={editable && !disabled}
            module={module}
          />
        </Col>
      )}
    </Row>
  );
};

export default AttachmentsComponent;
